import { ArrowCaret } from '@fs/zion-icon'
import { usePageView } from '@fs/zion-analytics'
import { Button, Cell, Grid, TextArea, TextField, H5 } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import { PersonLink } from '../../app/Links'
import Person from './person/Person'
import useAdminMerge from './useAdminMerge'

export default function AdminMerge() {
  usePageView({ page_detail: 'DMConsole:AdminMerge' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.adminMerge'))
  const [adminMergeState, helpers] = useAdminMerge()
  const {
    merge,
    mergeConstraint,
    possibleDuplicate,
    possibleDuplicateInput,
    showMergeData,
    showMergeSuccessMessage,
    survivingPerson,
    survivingPersonInput,
    mergedFieldIds,
  } = adminMergeState
  const {
    actionButtonOnClick,
    continueBtnOnClick,
    continueBtnDisabled,
    getErrorMessage,
    getValidMessage,
    finishMergeBtnOnClick,
    onPersonInputChange,
    toggleShowItemHiddenDetails,
    toggleShowMoreLessLifeSketch,
    setLifeSketchExpandedHeight,
    setChangeMessage,
  } = helpers
  return (
    <Grid>
      <Cell columns={6}>
        <H5 supplementary>
          {t(
            'tools.adminMerge.headings.main',
            'Merge two records in the same owner space when a Locked restriction prevents the merge on Family Tree. Move or add information to the surviving record.  Ordinances, reservations, sources, and Admin Labels will automatically transfer.'
          )}
        </H5>
      </Cell>
      <p columns={6} />
      <Cell columns={2}>
        <TextField
          id="possibleDuplicate"
          label={t('possibleDuplicate', 'Possible Duplicate')}
          type="text"
          value={possibleDuplicateInput}
          onChange={(e) => onPersonInputChange(e.target.value, false)}
          validMessage={getValidMessage(possibleDuplicateInput, possibleDuplicate)}
          errorMessage={getErrorMessage(possibleDuplicateInput, possibleDuplicate)}
        />
      </Cell>
      <Cell columns={2}>
        <TextField
          id="survivingPerson"
          label={t('person.merges.survivingPerson', 'Surviving Person')}
          type="text"
          value={survivingPersonInput}
          onChange={(e) => onPersonInputChange(e.target.value, true)}
          validMessage={getValidMessage(survivingPersonInput, survivingPerson)}
          errorMessage={getErrorMessage(survivingPersonInput, survivingPerson)}
        />
      </Cell>
      <p columns={6} />
      <Cell columns={2} alignY="middle">
        <Button Icon={ArrowCaret} emphasis="high" disabled={continueBtnDisabled} onClick={continueBtnOnClick}>
          {t('tools.adminMerge.buttons.continue', 'Continue')}
        </Button>
      </Cell>
      {mergeConstraint && (
        <Cell columns={12} alignY="middle">
          <TextField
            id="mergeConstraint"
            label={t('tools.adminMerge.labels.mergeConstraint', 'Merge Constraint')}
            type="text"
            value={mergeConstraint}
            disabled
            warningValidate={() =>
              t('tools.adminMerge.notifications.mergeConstraint', 'Merge contains the following constraint')
            }
          />
        </Cell>
      )}
      {showMergeSuccessMessage && (
        <Cell columns={12} alignY="middle">
          <H5 centered>
            {t('tools.adminMerge.notifications.successfullyMerged', 'Successfully merged')}{' '}
            <PersonLink personId={possibleDuplicate?.id} personName={possibleDuplicate?.name} />
            {' into '}
            <PersonLink personId={survivingPerson?.id} personName={survivingPerson?.name} />
          </H5>
        </Cell>
      )}
      <p columns={10} />
      {showMergeData && (
        <Grid>
          <Cell columns={6}>
            <Person
              actionButtonOnClick={actionButtonOnClick}
              person={merge.possibleDuplicate}
              toggleShowItemHiddenDetails={toggleShowItemHiddenDetails}
              toggleShowMoreLessLifeSketch={toggleShowMoreLessLifeSketch}
              setLifeSketchExpandedHeight={setLifeSketchExpandedHeight}
            />
          </Cell>
          <Cell columns={6}>
            <Person
              actionButtonOnClick={actionButtonOnClick}
              person={merge.survivingPerson}
              toggleShowItemHiddenDetails={toggleShowItemHiddenDetails}
              toggleShowMoreLessLifeSketch={toggleShowMoreLessLifeSketch}
              setLifeSketchExpandedHeight={setLifeSketchExpandedHeight}
            />
          </Cell>
          <Cell columns={12}>
            <TextArea
              label={t('tools.adminMerge.labels.reasonForMerge', 'Reason For Merge')}
              value={mergedFieldIds.attribution.changeMessage}
              onChange={(e) => setChangeMessage(e.target.value)}
              placeholder={t(
                'tools.adminMerge.placeholders.reasonForMerge',
                'Explain why you think this merge is correct.'
              )}
              disableResize
            />
          </Cell>
          <Cell columns={12}>
            <Button emphasis="high" onClick={finishMergeBtnOnClick}>
              {t('tools.adminMerge.buttons.finishMerge', 'Finish Merge')}
            </Button>
          </Cell>
        </Grid>
      )}
    </Grid>
  )
}
